import moment from 'moment-timezone'

import {
  getShortTransactionLogId,
  formatCurrency,
  formatDate,
  formatTime
} from '@smarttransit/common'

import {
  addParamsToLocation,
  generateKeywordsQueryForOr
} from '@smarttransit/common-client'

import { addAlert, isAuthorized } from '@/utilities/helpers'

import {
  findTotalVendorTransactionLogs,
  findVendorTransactionLogs,
  findTotalGeneralVendorTransactionLogs,
  findGeneralVendorTransactionLogs,
  expireTicketNow
} from '@/services/passenger-transaction-logs-service'

import {
  createUnusedBalanceRefund,
  createAgentCreditRefund
  // deleteUnusedBalanceRefund
} from '@/services/passengers-service'

import { getUser } from '@/services/users-service'
import { findPassengerTrips } from '@/services/passenger-trips-service'

export default {
  name: 'vendor-passenger-transactions',
  props: {
    signedInUser: Object,
    forceRootViewRefresh: Function
  },
  data () {
    return {
      apiInProgress: false,
      countInProgress: false,
      lastSearchRequestId: 0,
      searchKeywords: '',
      searchPhone: '',
      selectedVendor: '',
      metadataLoaded: false,
      selectedMetadata: null,
      expireNowRefundUnusedBalance: false,
      countryCurrency: null,
      dateFromMenu: null,
      dateFrom: null,
      dateToMenu: null,
      unusedBalanceRefundDateMenu: null,
      dateTo: null,
      headers: [],
      transactions: [],
      // When null, total is unknown (use next/previous pagination only)
      totalTransactions: null,
      createUnusedBalanceRefundDialog: false,
      newUnusedBalanceRefund: {},
      createAgentCreditRefundDialog: false,
      newAgentCreditRefund: {},
      logTypes: [
        { text: 'Purchased Ticket', value: 'purchased_ticket' },
        { text: 'Purchased Credit', value: 'purchased_credit' },
        { text: 'Purchased Gift', value: 'purchased_gift' },
        { text: 'Refund', value: 'refund' },
        { text: 'Unused Balance Refund', value: 'unused_balance_refund' }
      ],
      selectedLogTypes: [],
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        sortBy: 'dateRecorded'
      },
      isVendor: false,
      vendors: [ { text: 'MTN Ghana', value: 'mtnghana', metadata: { countryId: 'GHA' } }, { text: 'Airtel/Tigo Ghana', value: 'atigoghana', metadata: { countryId: 'GHA' } }, { text: 'Telecel Ghana', value: 'vodaghana', metadata: { countryId: 'GHA' } } ]
    }
  },
  computed: {
    computedDateFromFormatted () {
      return this.dateFrom ? formatDate(this.dateFrom) : ''
    },
    computedDateToFormatted () {
      return this.dateTo ? formatDate(this.dateTo) : ''
    },
    computedDateFromMax () {
      const fromDateAsMoment = this.dateFrom ? moment(this.dateFrom, 'Y-MM-DD') : null
      const toDateAsMoment = this.dateTo ? moment(this.dateTo, 'Y-MM-DD') : null
      if (toDateAsMoment && (!fromDateAsMoment || fromDateAsMoment.valueOf() <= toDateAsMoment.valueOf())) {
        return toDateAsMoment.subtract(1, 'day').format('Y-MM-DD')
      }
      return ''
    },
    computedDateToMin () {
      const fromDateAsMoment = this.dateFrom ? moment(this.dateFrom, 'Y-MM-DD') : null
      const toDateAsMoment = this.dateTo ? moment(this.dateTo, 'Y-MM-DD') : null
      if (fromDateAsMoment && (!toDateAsMoment || fromDateAsMoment.valueOf() <= toDateAsMoment.valueOf())) {
        return fromDateAsMoment.add(1, 'day').format('Y-MM-DD')
      }
      return ''
    },
    computedUnusedBalanceRefundDateFormatted () {
      return this.newUnusedBalanceRefund.dateCreated ? formatDate(this.newUnusedBalanceRefund.dateCreated) : ''
    },
    unusedBalanceRefundComputedDateMax () {
      return this.newUnusedBalanceRefund.dateCreated ? moment(this.newUnusedBalanceRefund.dateCreated).format('Y-MM-DD') : moment().format('Y-MM-DD')
    },
    isAdmin () {
      return isAuthorized(this.$props.signedInUser, 'admin')
    },
    isTotalTransactionsKnown () {
      return typeof this.totalTransactions === 'number'
    },
    tableTotalItems () {
      // Vuetify's v-data-table clamps `pagination.page` based on `total-items`.
      // When totals are unknown (we use custom next/prev), we must still provide a
      // non-zero "server items length" so the table doesn't reset back to page 1.
      if (this.isTotalTransactionsKnown) {
        return this.totalTransactions
      }

      const rowsPerPage = Number(this.pagination && this.pagination.rowsPerPage) || 10
      const page = Number(this.pagination && this.pagination.page) || 1
      return page * rowsPerPage
    },
    canGoToPreviousPage () {
      return this.pagination.page > 1 && !this.apiInProgress
    },
    canGoToNextPage () {
      // With unknown totals, the only safe heuristic is: if we got a full page, assume there may be more.
      return this.transactions && this.transactions.length === this.pagination.rowsPerPage && !this.apiInProgress
    },
    knownPageCount () {
      if (!this.isTotalTransactionsKnown) return 0
      const rowsPerPage = Number(this.pagination.rowsPerPage) || 10
      const total = Number(this.totalTransactions) || 0
      return Math.max(1, Math.ceil(total / rowsPerPage))
    },
    selectedPurchasedTickets () {
      return this.transactions.filter((o) => o.selected)
    }
  },
  mounted () {
    this.headers = [
      ...(this.isAdmin ? [{
        text: '',
        align: 'left',
        sortable: false
      }] : []),
      {
        text: 'Phone',
        align: 'left',
        sortable: false
      },
      // {
      //   text: 'Email',
      //   align: 'left',
      //   sortable: false
      // },
      {
        text: 'Amount',
        align: 'left',
        sortable: true,
        value: 'amount'
      },
      {
        text: 'Credit Before',
        align: 'left',
        sortable: true,
        value: 'profileBalanceBefore'
      },
      {
        text: 'After',
        align: 'left',
        sortable: true,
        value: 'profileBalanceAfter'
      },
      {
        text: 'Log Type',
        align: 'left',
        sortable: true,
        value: 'logType'
      },
      {
        text: 'Log Status',
        align: 'left',
        sortable: true,
        value: 'logStatus'
      },
      {
        text: 'Date Created',
        align: 'left',
        sortable: true,
        value: 'dateRecorded'
      }
    ].filter(Boolean)

    this.loadFromQueryParams()

    if (this.$props.signedInUser) {
      this.isVendor = this.$props.signedInUser.roles.find((o) => (o.name === 'vendor'))

      if (this.isVendor) {
        const permittedVendorTypes = ['mtnghana'] // this.$props.signedInUser.metadata?.vendorTypes || []
        this.vendors = this.vendors.filter((o) => (permittedVendorTypes.includes(o.value)))

        if (this.vendors.length === 1) {
          this.selectedVendor = this.vendors[0].value
        }
      }

      this.initCountryCurrency().then(() => {
        return this.searchTransactions()
      }).then(() => {
        this.componentMounted = true
      })
    }
  },
  watch: {
    selectedVendor () {
      this.lastParams = addParamsToLocation({ selectedVendor: this.selectedVendor }, this.$route, this.lastParams)
      if (this.componentMounted) {
        this.pagination.page = 1
        this.searchTransactions()
      }
    },
    selectedLogTypes () {
      this.lastParams = addParamsToLocation({ selectedLogTypes: this.selectedLogTypes }, this.$route, this.lastParams)
    },
    pagination (updatedPagination) {
      this.lastParams = addParamsToLocation(updatedPagination, this.$route, this.lastParams)
    }
  },
  methods: {
    loadFromQueryParams () {
      if (this.$route.query.descending) {
        this.pagination.descending = this.$route.query.descending
      }
      if (this.$route.query.page) {
        this.pagination.page = this.$route.query.page
      }
      if (this.$route.query.rowsPerPage) {
        this.pagination.rowsPerPage = this.$route.query.rowsPerPage
      }
      if (this.$route.query.sortBy) {
        this.pagination.sortBy = this.$route.query.sortBy
      }
      if (this.$route.query.searchPhone) {
        this.searchPhone = this.$route.query.searchPhone
      }
      if (this.$route.query.dateFrom) {
        this.dateFrom = this.$route.query.dateFrom
      }
      if (this.$route.query.dateTo) {
        this.dateTo = this.$route.query.dateTo
      }
      if (this.$route.query.selectedVendor) {
        this.selectedVendor = this.$route.query.selectedVendor
      }
    },
    async initCountryCurrency () {
      let vendor = this.vendors.find((o) => o.value === this.selectedVendor)

      if (!vendor) {
        vendor = this.vendors[0]
        if (this.isVendor) {
          this.selectedVendor = vendor.value
        }
      }

      this.countryCurrencies = await this.$store.getters.getCountryCurrencies
      this.countryCurrency = this.countryCurrencies[vendor.metadata.countryId]
    },
    onPagination () {
      if (this.componentMounted) {
        this.searchTransactions()
      }
    },
    goToPreviousPage () {
      if (!this.canGoToPreviousPage) return
      this.pagination.page = Number(this.pagination.page) - 1
      this.searchTransactions()
    },
    goToNextPage () {
      if (!this.canGoToNextPage) return
      this.pagination.page = Number(this.pagination.page) + 1
      this.searchTransactions()
    },
    viewMetadata (item) {
      this.metadataLoaded = true
      this.expireNowRefundUnusedBalance = false
      this.selectedMetadata = { ...item }

      if (this.selectedMetadata) {
        this.selectedMetadata.logMetadataParsed = item?.logMetadata && Object.keys(item.logMetadata).length ? { ...item.logMetadata } : { notice: 'No metadata available' }

        if (this.selectedMetadata.logMetadata.responseParams && typeof this.selectedMetadata.logMetadata.responseParams === 'object') {
          this.selectedMetadata.logMetadataParsed.responseParams = `<pre>${JSON.stringify(this.selectedMetadata.logMetadataParsed.responseParams, null, 2)}</pre>`
        }

        if (this.selectedMetadata.logMetadata.transportationProfileEventId) {
          findPassengerTrips({
            join: {
              relation: 'passengerTransactionLogTrips',
              scope: { where: { passengerTransactionLogId: this.selectedMetadata.id } }
            },
            include: { passengerTransactionLogTrips: 'passengerTrip' }
          }).then((result) => {
            if (result?.length) {
              const passengerTrip = result[0]
              passengerTrip.cachedTransportationProfileEvent.flatRateFee = passengerTrip.cachedTransportationProfileEvent.flatRateFee ? formatCurrency(passengerTrip.cachedTransportationProfileEvent.flatRateFee) : passengerTrip.cachedTransportationProfileEvent.flatRateFee
              passengerTrip.cachedTransportationProfileEvent.pickupTime = passengerTrip.cachedTransportationProfileEvent.pickupTime ? formatTime(passengerTrip.cachedTransportationProfileEvent.pickupTime) : passengerTrip.cachedTransportationProfileEvent.pickupTime
              this.selectedMetadata = { ...this.selectedMetadata, cachedTransportationProfileEvent: `<code>${JSON.stringify(passengerTrip.cachedTransportationProfileEvent, null, 2)}</code>` }
            }
          })
        }
      }
    },
    async expireTicketNow () {
      if (!this.isAdmin) {
        return addAlert({ message: 'Only admins can expire tickets', type: 'error', isModal: true })
      }

      if (!this.selectedMetadata || !this.selectedMetadata.id) {
        return addAlert({ message: 'No transaction selected', type: 'error', isModal: true })
      }

      if (this.selectedMetadata.logType !== 'purchased_ticket') {
        return addAlert({ message: 'Only purchased tickets can be expired', type: 'error', isModal: true })
      }

      if (this.selectedMetadata.logStatus === 'completed') {
        return addAlert({ message: 'Ticket is completed and cannot be expired', type: 'error', isModal: true })
      }

      const refundText = this.expireNowRefundUnusedBalance ? 'and refund remaining refundable amount' : 'without refund'
      if (!confirm(`Expire this ticket now ${refundText}?`)) {
        return
      }

      this.apiInProgress = true

      try {
        const result = await expireTicketNow({
          id: this.selectedMetadata.id,
          issueUnusedBalanceRefund: this.expireNowRefundUnusedBalance
        })

        // Refresh list + reflect latest status in modal
        await this.searchTransactions()

        if (this.selectedMetadata) {
          this.selectedMetadata = { ...this.selectedMetadata, logStatus: 'expired' }
        }

        const refunded = result && typeof result.refundedAmount === 'number' ? result.refundedAmount : 0
        const refundId = result && result.refundTransactionLogId ? result.refundTransactionLogId : null

        addAlert({
          message: refundId ? `Ticket expired. Refund created: ${getShortTransactionLogId(refundId)}${refunded ? ' (amount: ' + formatCurrency(refunded) + ')' : ''}` : `Ticket expired.${refunded ? ' Refunded amount: ' + formatCurrency(refunded) + ')' : ''}`,
          type: 'success',
          isModal: true
        })

        this.metadataLoaded = false
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.apiInProgress = false
      }
    },
    async viewUser (stUserId) {
      const user = await getUser({ id: stUserId })

      if (user && this.selectedMetadata) {
        this.selectedMetadata = { ...this.selectedMetadata, stUserLabel: `<code>${JSON.stringify({ phoneNumber: user.phoneNumber, email: user.email, firstName: user.firstName, lastName: user.lastName }, null, 2)}</code>` }
      }
    },
    async saveAgentCreditRefund ({
      newAgentCreditRefund
    }) {
      if (!confirm('Are you sure you want to refund this agent credit?')) {
        return
      }

      this.apiInProgress = true

      try {
        await createAgentCreditRefund({
          passengerProfileId: newAgentCreditRefund.passengerIdentifier.passengerProfileId,
          amount: newAgentCreditRefund.amount,
          agentProfileId: newAgentCreditRefund.agent.id
        })

        this.createAgentCreditRefundDialog = false
        this.newAgentCreditRefund = {}
        this.searchTransactions()

        addAlert({
          message: `Agent credit refund saved successfully`,
          type: 'success'
        })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error',
          isModal: true
        })
      } finally {
        this.apiInProgress = false
      }
    },
    async saveUnusedBalanceRefund ({
      selectedPurchasedTickets,
      newUnusedBalanceRefund
    }) {
      if (!confirm('Are you sure you want to create this unused balance refund?')) {
        return
      }

      this.apiInProgress = true
      let dateCreatedInAccra = null

      if (newUnusedBalanceRefund.dateCreated) {
        // set the dateCreated to 13:00:00 of the timezone "Africa/Accra" but make sure the date is the same, just in the specific timezone
        const dateCreatedAsMoment = moment.utc(newUnusedBalanceRefund.dateCreated)
        const timezone = 'Africa/Accra'
        const dateCreatedAsMomentInAccra = dateCreatedAsMoment.tz(timezone)
        dateCreatedInAccra = dateCreatedAsMomentInAccra.set('hour', 13).set('minute', 0).set('second', 0).toDate()
      }

      try {
        await createUnusedBalanceRefund({
          passengerProfileId: newUnusedBalanceRefund.passengerIdentifier.passengerProfileId,
          amount: newUnusedBalanceRefund.amount,
          refundFromTransactionLogIds: selectedPurchasedTickets.map((o) => o.id),
          dateCreated: dateCreatedInAccra?.toISOString()
        })

        this.createUnusedBalanceRefundDialog = false
        this.newUnusedBalanceRefund = {}
        this.searchTransactions()

        addAlert({
          message: `Unused balance refund saved successfully`,
          type: 'success'
        })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error',
          isModal: true
        })
      } finally {
        this.apiInProgress = false
      }
    },
    submitSearch () {
      this.lastParams = addParamsToLocation({ dateFrom: this.dateFrom, dateTo: this.dateTo, searchPhone: this.searchPhone }, this.$route, this.lastParams)
      this.pagination.page = 1
      if (this.componentMounted) {
        this.searchTransactions()
      }
    },
    marshallTransaction (o) {
      const amountClass = o.amount < 0 ? 'error--text' : ''
      const balanceBeforeClass = o.profileBalanceBefore < 0 ? 'error--text' : ''
      const balanceAfterClass = o.profileBalanceAfter < 0 ? 'error--text' : ''
      o.amountLabel = `<span class="${amountClass}">${this.countryCurrency.currencySymbol}${formatCurrency(o.amount)}</span>`
      o.profileBalanceBeforeLabel = `<span class="${balanceBeforeClass}">${formatCurrency(o.profileBalanceBefore)}</span>`
      o.profileBalanceAfterLabel = `<span class="${balanceAfterClass}">${formatCurrency(o.profileBalanceAfter)}</span>`
      o.phoneLabel = o.passengerIdentifier ? o.passengerIdentifier.phoneNumber : (o.stUser?.phone || '')
      o.parsedId = getShortTransactionLogId(o.id)

      if (o.logType === 'refund') {
        o.logTypeIcon = 'fas fa-undo'
      } else if (o.logType === 'unused_balance_refund') {
        o.logTypeIcon = 'fas fa-recycle'
      } else if (o.logType === 'purchased_gift') {
        o.logTypeIcon = 'fas fa-gift'
      } else if (o.logType === 'purchased_ticket') {
        o.logTypeIcon = 'fas fa-ticket-alt'
      } else if (o.logType === 'purchased_credit') {
        o.logTypeIcon = 'fas fa-money-bill-wave'
      } else {
        o.logTypeIcon = ''
      }

      switch (o.logStatus) {
        case 'error':
          o.logStatusIcon = 'fas fa-exclamation-triangle'
          o.logStatusTooltip = 'Error: ' + o.logMetadata.error
          break
        case 'confirmed':
        case 'completed':
          o.logStatusIcon = 'fas fa-check'
          o.logStatusTooltip = o.logStatus === 'confirmed' ? 'Confirmed' : 'Completed'
          break
        case 'timedout':
          o.logStatusIcon = 'fas fa-hourglass-end'
          o.logStatusTooltip = 'Timeout occurred while waiting for vendor feedback'
          break
        case 'expired':
          o.logStatusIcon = 'fas fa-calendar-times'
          o.logStatusTooltip = 'Expired'
          break
        default:
          o.logStatusIcon = 'fas fa-info-circle'
          o.logStatusTooltip = o.logStatus
      }

      o.dateCreatedLabel = `${formatTime(o.dateCreated)}, ${formatDate(o.dateCreated)}`
      o.dateRecordedLabel = `${formatTime(o.dateRecorded)}, ${formatDate(o.dateRecorded)}`
      o.dateUpdatedLabel = o.dateUpdated ? `${formatTime(o.dateUpdated)}, ${formatDate(o.dateUpdated)}` : o.dateCreatedLabel

      return o
    },
    async searchTransactions () {
      const requestId = (this.lastSearchRequestId || 0) + 1
      this.lastSearchRequestId = requestId

      const { sortBy, descending, page, rowsPerPage } = this.pagination
      const offset = page === 1 ? 0 : (page * rowsPerPage) - rowsPerPage

      let params = {
        vendor: null,
        filter: {
          limit: rowsPerPage,
          offset,
          where: { logType: { neq: 'ad_hoc_revenue' } }
        }
      }

      if (!sortBy) {
        params.filter.order = ['dateRecorded DESC', 'dateUpdated DESC']
      } else {
        params.filter.order = `${sortBy} ${descending ? 'DESC' : 'ASC'}`
      }

      if (this.searchKeywords) {
        params.stUserKeywords = { or: generateKeywordsQueryForOr(this.searchKeywords, ['value']) }
      }

      if (this.selectedVendor) {
        params.vendor = this.selectedVendor
      }

      if (this.selectedLogTypes.length) {
        params.filter.where.logType = {
          inq: this.selectedLogTypes,
          neq: 'ad_hoc_revenue'
        }
      }

      if (this.searchPhone) {
        params.searchPhone = this.searchPhone
      }

      if (this.dateFrom) {
        if (!params.filter.where.and) {
          params.filter.where.and = []
        }

        params.filter.where.and.push({ dateCreated: { gte: moment.utc(this.dateFrom, 'Y-MM-DD').toDate().toISOString() } })
      }

      if (this.dateTo) {
        if (!params.filter.where.and) {
          params.filter.where.and = []
        }

        params.filter.where.and.push({ dateCreated: { lte: moment.utc(this.dateTo, 'Y-MM-DD').add(23, 'hours').add(59, 'minutes').toDate().toISOString() } })
      }

      this.apiInProgress = true
      this.countInProgress = true
      // Reset total early so list rendering never waits on count.
      this.totalTransactions = null

      try {
        let transactions

        if (params.vendor) {
          transactions = await findVendorTransactionLogs(params)

          // Fire-and-forget count. If it times out or errors, we keep totalTransactions as null.
          findTotalVendorTransactionLogs({
            where: params.filter.where,
            stUserKeywords: params.stUserKeywords,
            searchPhone: params.searchPhone,
            vendor: params.vendor,
            timeoutMsecs: 2000,
            returnNullOnTimeout: true
          }).then((result) => {
            if (this.lastSearchRequestId !== requestId) return
            this.totalTransactions = result && typeof result.count === 'number' ? result.count : null
          }).catch(() => {
            if (this.lastSearchRequestId !== requestId) return
            this.totalTransactions = null
          }).finally(() => {
            if (this.lastSearchRequestId !== requestId) return
            this.countInProgress = false
          })
        } else {
          if (!Object.keys(params.filter.where).length) {
            params.filter.where = null
          }

          transactions = await findGeneralVendorTransactionLogs(params)

          // Fire-and-forget count. If it times out or errors, we keep totalTransactions as null.
          findTotalGeneralVendorTransactionLogs({
            where: params.filter.where,
            searchPhone: params.searchPhone,
            timeoutMsecs: 2000,
            returnNullOnTimeout: true
          }).then((result) => {
            if (this.lastSearchRequestId !== requestId) return
            this.totalTransactions = result && typeof result.count === 'number' ? result.count : null
          }).catch(() => {
            if (this.lastSearchRequestId !== requestId) return
            this.totalTransactions = null
          }).finally(() => {
            if (this.lastSearchRequestId !== requestId) return
            this.countInProgress = false
          })
        }

        if (this.lastSearchRequestId !== requestId) {
          return
        }

        this.transactions = transactions.map(o => {
          return this.marshallTransaction(o)
        })

        this.apiInProgress = false
      } catch (err) {
        this.apiInProgress = false
        this.countInProgress = false

        if (err && !err.error) {
          addAlert({
            message: `Error in getting transactions: ${err && err.message ? err.message : JSON.stringify(err)}`,
            type: 'error'
          })
        }
      }
    }
  },
  beforeDestroy () {}
}
